import { Check } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Card, CardContent } from "@/components/ui/card";
import { ErrorMessage } from "@/components/ui/error";
import { LoadingSpinner } from "@/components/ui/spinner";
import { TypographyLarge, TypographySmall } from "@/components/ui/typography";
import { useGetAllInvoices } from "@/queries/invoices/get-all";

type Props = {
  invoiceIds: string[];
};

const _PaymentSuccess = ({ invoiceIds }: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const config = useMemo(() => {
    return {
      enabled: false,
      skipDbClear: true,
    };
  }, []);

  const { fetchInvoices } = useGetAllInvoices(config);

  const handleFetchInvoices = useCallback(async () => {
    if (invoiceIds.length > 0) {
      setLoading(true);

      try {
        await fetchInvoices({
          query: {
            invoiceId: {
              value: invoiceIds,
              op: "in",
            },
          },
        });
      } catch (error) {
        setError(error as Error);
      } finally {
        setLoading(false);
      }
    }
  }, [invoiceIds]);

  useEffect(() => {
    handleFetchInvoices();
  }, [handleFetchInvoices]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px] p-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-[400px] p-4">
        <ErrorMessage />
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-[400px] p-4">
      <Card className="w-full max-w-lg shadow-md">
        <CardContent className="flex flex-col items-center text-center pt-10 pb-10">
          <div className="bg-green-100 rounded-full p-6 mb-8">
            <Check className="h-10 w-10 text-green-600" strokeWidth={3} />
          </div>
          <TypographyLarge>{t("payment_success.title")}</TypographyLarge>
          <TypographySmall className="text-muted-foreground mt-2">
            {t("payment_success.description")}
          </TypographySmall>
        </CardContent>
      </Card>
    </div>
  );
};

export const PaymentSuccess = memo(_PaymentSuccess);
